/**
 * Admin > Business Cards
 * Manage company-wide business card templates visible to all users.
 */
import { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../../services/auth'
import {
  listAllBusinessCards,
  createBusinessCard,
  updateBusinessCard,
  deleteBusinessCard,
} from '../../services/crm'
import type { BusinessCardTemplate, NotificationSettings } from '../../types'
import Modal from '../../components/Modal'
import SendCardModal from '../../components/SendCardModal'
import { getNotificationSettings } from '../../services/admin'
import { CardForm, BusinessCardPreview } from '../../components/businessCards'
import {
  EMPTY_FORM,
  companyCardDefaults,
  type CardFormState,
} from '../../utils/businessCardForm'

export default function AdminBusinessCardsPage() {
  const { user } = useAuth()

  const [cards, setCards] = useState<BusinessCardTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  // Company Info, used only to pre-fill a new card. A failure to load it must
  // not stop the page working, so it stays null and the form opens empty.
  const [companyInfo, setCompanyInfo] = useState<NotificationSettings | null>(null)

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [editingCard, setEditingCard] = useState<BusinessCardTemplate | null>(null)
  const [form, setForm] = useState<CardFormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')

  // Send this card to a typed address (no CRM record involved).
  const [sendCard, setSendCard] = useState<BusinessCardTemplate | null>(null)

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<BusinessCardTemplate | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    load()
  }, [])

  async function load() {
    setLoading(true)
    setLoadError('')
    try {
      const all = await listAllBusinessCards()
      setCards(all.filter((c) => c.card_type === 'company'))
      try {
        setCompanyInfo(await getNotificationSettings())
      } catch {
        setCompanyInfo(null)
      }
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load cards')
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setEditingCard(null)
    setForm(companyCardDefaults(companyInfo))
    setFormError('')
    setShowModal(true)
  }

  function openEdit(card: BusinessCardTemplate) {
    setEditingCard(card)
    setForm({
      name: card.name,
      sender_name: card.sender_name,
      title: card.title ?? '',
      email: card.email ?? '',
      phone: card.phone ?? '',
      greeting_text: card.greeting_text ?? '',
    })
    setFormError('')
    setShowModal(true)
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setFormError('')
    try {
      if (editingCard) {
        const updated = await updateBusinessCard(editingCard.id, {
          name: form.name.trim(),
          sender_name: form.sender_name.trim(),
          title: form.title.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          greeting_text: form.greeting_text.trim() || undefined,
        })
        setCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
      } else {
        const created = await createBusinessCard({
          name: form.name.trim(),
          card_type: 'company',
          sender_name: form.sender_name.trim(),
          title: form.title.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          greeting_text: form.greeting_text.trim() || undefined,
        })
        setCards((prev) => [...prev, created])
      }
      setShowModal(false)
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await deleteBusinessCard(deleteTarget.id)
      setCards((prev) => prev.filter((c) => c.id !== deleteTarget.id))
      setDeleteTarget(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setDeleting(false)
    }
  }

  async function handleToggleActive(card: BusinessCardTemplate) {
    try {
      const updated = await updateBusinessCard(card.id, { is_active: !card.is_active })
      setCards((prev) => prev.map((c) => (c.id === updated.id ? updated : c)))
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update card')
    }
  }

  if (!user?.is_superuser) return <Navigate to="/dashboard" replace />

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Company Business Cards</h2>
          <p className="mt-1 text-sm text-gray-500">
            Shared cards available to all users when contacting leads and clients.
          </p>
        </div>
        <button onClick={openCreate} className="btn btn-primary">
          + New Card
        </button>
      </div>

      {loadError && <div className="alert alert-error mb-4">{loadError}</div>}

      {loading ? (
        <div className="text-gray-500">Loading…</div>
      ) : cards.length === 0 ? (
        <div className="card text-center text-gray-500 py-12">
          No company business cards yet.{' '}
          <button onClick={openCreate} className="text-blue-600 underline">
            Create the first one.
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {cards.map((card) => (
            <BusinessCardPreview
              key={card.id}
              card={card}
              onEdit={() => openEdit(card)}
              onDelete={() => setDeleteTarget(card)}
              onToggleActive={() => handleToggleActive(card)}
              onSend={() => setSendCard(card)}
            />
          ))}
        </div>
      )}

      {sendCard && (
        <SendCardModal
          fixedCard={sendCard}
          onClose={() => setSendCard(null)}
        />
      )}

      {/* Create / Edit modal */}
      {showModal && (
        <Modal
          titleId="business-card-modal-title"
          title={editingCard ? 'Edit Company Card' : 'New Company Card'}
          onClose={() => setShowModal(false)}
          showCloseButton
        >
          <CardForm
            form={form}
            setForm={setForm}
            onSubmit={handleSave}
            saving={saving}
            error={formError}
            submitLabel={editingCard ? 'Save Changes' : 'Create Card'}
          />
        </Modal>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <Modal
          titleId="delete-card-title"
          title="Delete Card"
          onClose={() => setDeleteTarget(null)}
          showCloseButton
        >
          <p className="text-gray-700 mb-4">
            Delete <strong>{deleteTarget.name}</strong>? This cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}
